/**
 * @file Settles a rejected analytics callable call. Consumers:
 * `downloadAnalyticsReportRunPdf`, `emailAnalyticsReportRun` and
 * `recomputeAnalytics`. Only a `resource-exhausted` rejection whose
 * `details.retryAt` is a string is a cooldown; any other rejection, including
 * a failure after the claim that also carries `retryAt`, fails with its
 * message.
 */
import type { AnalyticsCallFailure } from '@/analytics';
import { readCallableError } from '@/workflows/runtime/readCallableError';

const retryAtOf = (error: unknown): string | undefined =>
  error instanceof Error &&
  'details' in error &&
  typeof error.details === 'object' &&
  error.details !== null &&
  'retryAt' in error.details &&
  typeof error.details.retryAt === 'string'
    ? error.details.retryAt
    : undefined;

export const analyticsCallFailure = (error: unknown): AnalyticsCallFailure => {
  const { code, message } = readCallableError(error);
  const retryAt = retryAtOf(error);
  if (code === 'resource-exhausted' && retryAt !== undefined)
    return { status: 'coolingDown', retryAt };
  return { status: 'failed', message: message || 'The call failed.' };
};
